<!--
  Customer Portal — Connected Suppliers directory (Story 3.7, FR14 / UC-PROMO-009 / AC-SM-01).
  Read-only directory built from FRD §5.3. Theme tokens only (no prototype hex); no $ (BR-07);
  no disconnect/edit/delete actions. A pending invitation never changes the connected directory.
-->
<section class="pf-content" aria-labelledby="pf-suppliers-heading">
  <nav class="pf-settings-breadcrumb">
    <a routerLink="/customer-pricing" class="pf-settings-back">← Back to Customer Pricing</a>
  </nav>

  <!-- Header row: title + the shared in-page invitation trigger. -->
  <div class="pf-page-header">
    <h1 id="pf-suppliers-heading">Connected Suppliers</h1>
    <button
      type="button"
      class="pf-connect-supplier"
      aria-haspopup="dialog"
      aria-controls="supplier-invitation-dialog"
      (click)="openInvitation()"
    >+ Connect Supplier</button>
  </div>
  <p class="pf-page-description">View the suppliers connected to your customer pricing reviews.</p>

  <p
    *ngIf="invitationOutcome?.kind === 'success'"
    class="pf-invitation-outcome pf-invitation-outcome--success"
    role="status"
    aria-live="polite"
  >{{ invitationOutcome?.message }}</p>

  <p *ngIf="loadError" class="pf-empty" role="alert">
    Couldn't load your connected suppliers. Please reload the page and try again.
  </p>

  <div *ngIf="loading" class="pf-skeleton" aria-busy="true" aria-label="Loading connected suppliers">
    <div class="pf-skeleton-row"></div>
    <div class="pf-skeleton-row"></div>
    <div class="pf-skeleton-row"></div>
  </div>

  <!-- Empty state — no connected suppliers yet (design §5.3). -->
  <div *ngIf="isEmpty" class="pf-suppliers-empty">
    <p class="pf-suppliers-empty-title">No suppliers connected yet.</p>
    <button
      type="button"
      class="pf-connect-supplier"
      aria-haspopup="dialog"
      aria-controls="supplier-invitation-dialog"
      (click)="openInvitation()"
    >+ Connect Supplier</button>
  </div>

  <!-- Directory grid — Supplier Name / Contact Email / Active Reviews / Status / Joined. -->
  <dx-data-grid
    *ngIf="!loading && !loadError && suppliers.length > 0"
    class="pf-suppliers-grid"
    [dataSource]="suppliers"
    keyExpr="supplier_code"
    [showBorders]="true"
    [columnAutoWidth]="true"
    [wordWrapEnabled]="false"
    [hoverStateEnabled]="true"
    aria-label="Connected suppliers"
  >
    <dxo-paging [enabled]="false"></dxo-paging>
    <dxo-sorting mode="single"></dxo-sorting>

    <dxi-column
      dataField="supplier_name"
      caption="Supplier Name"
      cellTemplate="nameTemplate"
      [allowSorting]="true"
      sortOrder="asc"
    ></dxi-column>
    <dxi-column
      dataField="email"
      caption="Contact Email"
      [allowSorting]="true"
      cellTemplate="emailTemplate"
    ></dxi-column>
    <dxi-column
      dataField="active_reviews_count"
      caption="Active Reviews"
      dataType="number"
      alignment="right"
      [allowSorting]="true"
    ></dxi-column>
    <dxi-column
      dataField="status"
      caption="Status"
      alignment="center"
      [allowSorting]="false"
      cellTemplate="statusTemplate"
    ></dxi-column>
    <!-- joined_date arrives pre-formatted as the latest accepted invitation date (YYYY-MM-DD),
         which is already display-ready and lexically sortable — no dataType="date" so DevExtreme does
         not reparse it to a locale-shifted Date. -->
    <dxi-column
      dataField="joined_date"
      caption="Joined"
      alignment="right"
      [allowSorting]="true"
      cssClass="pf-col-muted"
    ></dxi-column>

    <!-- Supplier name — bold, left. -->
    <div *dxTemplate="let cell of 'nameTemplate'">
      <span class="pf-supplier-name">{{ cell.data.supplier_name }}</span>
    </div>

    <!-- Contact email — em dash when none on file. -->
    <div *dxTemplate="let cell of 'emailTemplate'">
      <span *ngIf="cell.data.email; else noEmail">{{ cell.data.email }}</span>
      <ng-template #noEmail><span class="pf-col-muted">—</span></ng-template>
    </div>

    <!-- Status — the Story 1.5 status badge, "Active" (green). -->
    <div *dxTemplate="let cell of 'statusTemplate'">
      <app-status-badge status="active"></app-status-badge>
    </div>
  </dx-data-grid>

  <dx-popup
    id="supplier-invitation-dialog"
    cssClass="pf-supplier-invitation-popup"
    [(visible)]="invitationVisible"
    [width]="'min(560px, calc(100vw - 32px))'"
    height="auto"
    [maxHeight]="'calc(100vh - 32px)'"
    [showTitle]="true"
    title="Connect Supplier"
    [dragEnabled]="false"
    [hideOnOutsideClick]="false"
    [showCloseButton]="!invitationSubmitting"
    (onHiding)="onInvitationHiding($event)"
  >
    <form
      *dxTemplate="let data of 'content'"
      class="pf-invitation-dialog-content"
      [attr.aria-busy]="invitationSubmitting"
      (submit)="onInvitationSubmit($event)"
    >
      <p class="pf-invitation-intro">
        Choose an eligible supplier and enter the email address for one supplier contact.
      </p>

      <p *ngIf="candidatesLoading" class="pf-invitation-state" role="status" aria-live="polite">
        Loading eligible suppliers…
      </p>

      <div *ngIf="candidateLoadError" class="pf-invitation-state pf-invitation-state--error" role="alert">
        <p>Couldn't load eligible suppliers. Please try again.</p>
        <dx-button
          text="Retry supplier list"
          stylingMode="outlined"
          [disabled]="candidatesLoading"
          (onClick)="retryCandidates()"
        ></dx-button>
      </div>

      <p
        *ngIf="!candidatesLoading && !candidateLoadError && candidates.length === 0"
        class="pf-invitation-state"
        role="status"
      >No eligible suppliers found.</p>

      <div *ngIf="!candidatesLoading && !candidateLoadError && candidates.length > 0" class="pf-invitation-form">
        <div class="pf-invitation-field">
          <label class="pf-invitation-label" for="supplier-invitation-supplier">Supplier</label>
          <dx-select-box
            [dataSource]="candidates"
            valueExpr="id"
            [displayExpr]="candidateDisplay"
            itemTemplate="candidateItem"
            [value]="selectedSupplierId"
            [searchEnabled]="true"
            searchMode="contains"
            [searchExpr]="candidateSearchExpressions"
            [showClearButton]="true"
            noDataText="No eligible suppliers found."
            placeholder="Search by supplier name or code"
            [disabled]="invitationSubmitting"
            [inputAttr]="supplierInputAttributes"
            (onValueChanged)="onSupplierChanged($event)"
          >
            <div *dxTemplate="let candidate of 'candidateItem'" class="pf-candidate-option">
              <span>{{ candidate.supplier_name }}</span>
              <span class="pf-candidate-code">{{ candidate.supplier_code }}</span>
            </div>
          </dx-select-box>
          <p
            id="supplier-invitation-supplier-error"
            class="pf-field-error"
            role="alert"
            *ngIf="supplierValidationError"
          >{{ supplierValidationError }}</p>
        </div>

        <div class="pf-invitation-field">
          <label class="pf-invitation-label" for="supplier-invitation-email">Contact email</label>
          <dx-text-box
            mode="email"
            [value]="invitationEmail"
            [maxLength]="255"
            valueChangeEvent="input"
            placeholder="contact@example.com"
            [disabled]="invitationSubmitting"
            [inputAttr]="emailInputAttributes"
            (onValueChanged)="onEmailChanged($event)"
          ></dx-text-box>
          <p
            id="supplier-invitation-email-error"
            class="pf-field-error"
            role="alert"
            *ngIf="emailValidationError"
          >{{ emailValidationError }}</p>
        </div>
      </div>

      <p
        *ngIf="invitationOutcome?.kind === 'error'"
        class="pf-invitation-outcome pf-invitation-outcome--error"
        role="alert"
        aria-live="assertive"
      >{{ invitationOutcome?.message }}</p>

      <p
        *ngIf="invitationSubmitting"
        class="pf-invitation-sending"
        role="status"
        aria-live="polite"
      >Sending…</p>

      <div class="pf-invitation-actions">
        <dx-button
          text="Cancel"
          stylingMode="text"
          [disabled]="invitationSubmitting"
          (onClick)="cancelInvitation()"
        ></dx-button>
        <dx-button
          text="Send Invitation"
          type="default"
          [useSubmitBehavior]="true"
          [disabled]="invitationSubmitting || candidatesLoading || candidateLoadError || candidates.length === 0"
        ></dx-button>
      </div>
    </form>
  </dx-popup>
</section>
